import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { User } from '~/components/Shared/User/User';
import { ActionsMenu } from '~/eds/src/components/ActionsMenu/ActionsMenu';
import { Button } from '~/eds/src/components/Button/Button';
import { Chip } from '~/eds/src/components/Chip/Chip';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { PilotId as EdsPilotId } from '~/eds/src/types/PilotId';
import { StatusType } from '~/eds/src/types/StatusType';
import { slices } from '~/redux/slices';
import { Nullable } from '~/types/Nullable';
import { PilotId } from '~/types/PilotId';
import { externalUserToUser } from '~/utils/user';

import { TicketUserAssignment } from './TicketUserAssignment';

interface Props {
  userId: Nullable<PilotId>;
  externalUser: Nullable<{ name: string; email: string }>;
  placeholder?: string;
  chip?: {
    text: string;
    status?: StatusType;
  };
  actions: {
    reassign: boolean;
    assign: boolean;
  };
  onAssign?: (userId: EdsPilotId) => void;
  allAssignedUserIds?: EdsPilotId[];
}

export const TicketUserItem = ({
  userId,
  externalUser,
  placeholder,
  chip,
  actions,
  onAssign,
  allAssignedUserIds = [],
  children,
}: React.PropsWithChildren<Props>) => {
  const currentUser = useSelector(
    slices.currentUser.selectors.selectCurrentUser,
  );
  const intl = useIntl();
  const [showAssignmentSelect, setShowAssignmentSelect] = useState(false);

  const handleActionClick = (value: string) => {
    if (value === 're-assign') {
      setShowAssignmentSelect(true);
    } else if (value === 'assign-to-me') {
      onAssign?.(currentUser.id);
    }
  };

  const handleCancelAssignment = () => {
    setShowAssignmentSelect(false);
  };

  const handleAssignmentChange = (newAssigneeId: EdsPilotId) => {
    setShowAssignmentSelect(false);
    onAssign?.(newAssigneeId);
  };

  const getActionsMenu = () => {
    const items = [];
    if (actions.reassign) {
      items.push({
        label: intl.formatMessage({
          id: 'TicketUserItem.reassign',
          defaultMessage: 'Re-assign',
          description: 'Re-assign internal user action',
        }),
        value: 're-assign',
      });
    }

    if (actions.assign) {
      items.push({
        label: intl.formatMessage({
          id: 'TicketUserItem.assignToMe',
          defaultMessage: 'Assign to me',
          description: 'Assign internal user to current user',
        }),
        value: 'assign-to-me',
        disabled: userId === currentUser.id,
      });
    }

    return items;
  };

  const getActionButtons = () => {
    const actionButtons = [];
    if (actions.assign && !externalUser?.email) {
      actionButtons.push({
        label: intl.formatMessage({
          id: 'TicketUserItem.assign',
          defaultMessage: 'Assign',
          description: 'Assign internal user to ticket user item',
        }),
        onClick: () => setShowAssignmentSelect(true),
      });
    }

    return actionButtons;
  };

  const actionsMenu = getActionsMenu();
  const actionButtons = getActionButtons();

  // Adobe Sign can return only email, and no name. So special case to show only the email and not External User fallback
  const isAssignedExternalUserWithNoName =
    externalUser && !userId && !externalUser.name && externalUser.email;

  return (
    <Layout flex={1} align="center" justify="space-between" p={4}>
      <Layout align="center" spacing={2}>
        {isAssignedExternalUserWithNoName ? (
          <Text whiteSpace="nowrap" variant="body-bold">
            {externalUser.email}
          </Text>
        ) : (
          <User
            mode="info"
            id={userId}
            user={
              externalUser && !userId
                ? externalUserToUser(externalUser)
                : undefined
            }
            placeholder={placeholder}
          />
        )}
        {chip && <Chip text={chip.text} status={chip.status} />}
      </Layout>
      <Layout align="center" spacing={3}>
        {showAssignmentSelect ? (
          <TicketUserAssignment
            assignedUserId={userId}
            excludedUserIds={allAssignedUserIds}
            onAssignmentChange={handleAssignmentChange}
            onCancel={handleCancelAssignment}
          />
        ) : (
          <>
            {actionButtons.length > 0 &&
              actionButtons.map((button) => (
                <Button
                  key={button.label}
                  variant="action"
                  text={button.label}
                  onClick={button.onClick}
                />
              ))}
            {children}
            {actionsMenu.length > 0 && (
              <ActionsMenu
                id={`workflow_ticket_user_item_id=${
                  userId ?? externalUser?.email
                }`}
                name={`workflow_ticket_user_item_id=${
                  userId ?? externalUser?.email
                }`}
                actions={actionsMenu}
                onActionClick={handleActionClick}
              />
            )}
          </>
        )}
      </Layout>
    </Layout>
  );
};
